import React from "react";
import {
  Box, Checkbox, Collapse, FormControlLabel, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import { resolvePhaseIcon } from "../../../../Components/IconPicker/IconPicker";
import type { EditorPhase } from "./WorkflowEditorBody";

// Batch 7C §7C.5 — Waiting phase card. Sits at the bottom of the phase
// list. Grey Waiting chip, no resulting-status chip, no accuracy criteria.
// The four controls map 1:1 to the columns Batch 17 shipped:
//   autoOpenEnabled            → the parent checkbox
//   autoOpenMaxSimultaneous    → integer input
//   autoOpenInitialPhaseIndex  → phase-chip select (0-based array index)
//   autoOpenDirection          → select — only 'first_waiting' for now
//                                because the client's full option list
//                                is still unanswered; the control
//                                renders with the one option rather
//                                than inventing others.
//
// The label chip row "…targets in the {chip}{chip} phases" renders live
// from phases.filter(countsTowardAutoOpenLimit) — never a hardcoded
// string. Kerthiga renames a counted phase → the chip re-renders. She
// demotes a phase from counted → it drops out.

export type WaitingPhaseCardProps = {
  phases: EditorPhase[];
  autoOpenEnabled: boolean;
  autoOpenMaxSimultaneous: number | null;
  autoOpenInitialPhaseIndex: number | null;
  autoOpenDirection: string | null;
  onChange: (patch: {
    autoOpenEnabled?: boolean;
    autoOpenMaxSimultaneous?: number | null;
    autoOpenInitialPhaseIndex?: number | null;
    autoOpenDirection?: string | null;
  }) => void;
  readOnly?: boolean;
};

// Small inline phase chip: coloured pill, phase icon, phase name. Matches
// the header chip on PhaseCard so the settings copy reads as pointing at
// the actual phase objects.
function PhaseChipInline({ p }: { p: EditorPhase }) {
  const Ic = resolvePhaseIcon(p.icon);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5,
        px: 0.75, py: 0.15,
        borderRadius: `${LegacyTokens.radius.chip}px`,
        backgroundColor: p.colour || LegacyTokens.blue,
        color: LegacyTokens.surface,
        fontSize: 12, fontWeight: 600,
        lineHeight: 1.6,
        verticalAlign: "middle",
      }}
    >
      <Ic sx={{ fontSize: 12 }} />
      {p.phaseName || "Unnamed"}
    </Box>
  );
}

export default function WaitingPhaseCard({
  phases,
  autoOpenEnabled,
  autoOpenMaxSimultaneous,
  autoOpenInitialPhaseIndex,
  autoOpenDirection,
  onChange,
  readOnly,
}: WaitingPhaseCardProps) {
  const counted = phases.filter((p) => p.countsTowardAutoOpenLimit && p.phaseName.trim());
  const noCounted = counted.length === 0;

  // Options for the initial-phase select: exclude soft-deleted (not in
  // the array at all since the FE only holds active edits) and inactive
  // phases. Kerthiga can pick any active, named phase — the engine will
  // resolve to its FK by array index after save.
  const initialOptions = phases
    .map((p, index) => ({ p, index }))
    .filter(({ p }) => p.isActive && p.phaseName.trim());

  const selectedInitial =
    autoOpenInitialPhaseIndex !== null
      ? initialOptions.find((o) => o.index === autoOpenInitialPhaseIndex)
      : undefined;

  // If the previously-picked initial is no longer in the eligible set
  // (deactivated / removed), surface the degradation rather than swallow
  // it — same principle the FK ON DELETE SET NULL applies at the DB
  // layer. The dropdown shows an inline warning; the value falls back
  // to null on the next save (backend already resolves an out-of-range
  // index to null).
  const initialGone =
    autoOpenInitialPhaseIndex !== null && !selectedInitial;

  return (
    <Box
      data-testid="waiting-phase-card"
      sx={{
        border: `1px solid ${LegacyTokens.line}`,
        borderRadius: `${LegacyTokens.radius.card}px`,
        backgroundColor: LegacyTokens.surface,
        p: 2,
      }}
    >
      {/* Header — grey Waiting chip, no resulting-status chip, no
         accuracy criteria per §7C.5. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            px: 1.25, py: 0.5,
            borderRadius: `${LegacyTokens.radius.chip}px`,
            backgroundColor: LegacyTokens.canvas2,
            color: B13Ink.ink2,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Waiting</Typography>
        </Box>
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={autoOpenEnabled}
            disabled={readOnly}
            onChange={(e) => onChange({ autoOpenEnabled: e.target.checked })}
          />
        }
        label={
          <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
            Automatically open new targets when targets are mastered
          </Typography>
        }
      />

      <Collapse in={autoOpenEnabled} unmountOnExit>
        <Box sx={{ pl: 4, mt: 1 }}>
          <Stack spacing={1.75}>
            {/* Max. simultaneous — chip label rendered from data.
               Field stays editable even when noCounted; per the design,
               the guard is on the engine (no-op + warn), not the field,
               so Kerthiga can enter a value before fixing phases without
               losing it on the round-trip. */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
                Max. number of simultaneous targets in
              </Typography>
              <TextField
                size="small"
                type="number"
                value={autoOpenMaxSimultaneous ?? ""}
                disabled={readOnly}
                onChange={(e) =>
                  onChange({
                    autoOpenMaxSimultaneous: e.target.value === "" ? null : Math.max(1, Number(e.target.value)),
                  })
                }
                inputProps={{ min: 1, style: { width: 56, textAlign: "right" } }}
                variant="standard"
              />
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
                {noCounted ? (
                  "counted phases"
                ) : (
                  <>
                    {counted.map((p, i) => (
                      <React.Fragment key={`${p.phaseName}-${i}`}>
                        <PhaseChipInline p={p} />
                        {i < counted.length - 1 && (
                          <Typography component="span" sx={{ fontSize: 13, color: LegacyTokens.ink3, mx: 0.5 }}>
                            {i === counted.length - 2 ? "and" : ","}
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                    <Typography component="span" sx={{ fontSize: 13, color: LegacyTokens.ink2, ml: 0.75 }}>
                      phases
                    </Typography>
                  </>
                )}
              </Typography>
            </Stack>

            {noCounted && (
              <Typography sx={{ fontSize: 12, color: LegacyTokens.amberText, fontStyle: "italic" }}>
                No phases are marked as counting toward the auto-open limit — set
                “Counts toward auto-open limit” on individual phases.
              </Typography>
            )}

            {/* Initial status — renders as the phase chip via the select's
               displayValue, not plain text. */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, minWidth: 220 }}>
                Initial status when opening new targets
              </Typography>
              <TextField
                select
                size="small"
                value={autoOpenInitialPhaseIndex ?? ""}
                disabled={readOnly}
                onChange={(e) =>
                  onChange({
                    autoOpenInitialPhaseIndex: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { height: 36 } }}
                SelectProps={{
                  renderValue: (v) => {
                    if (v === "" || v === null || v === undefined) return <em>(none)</em>;
                    const opt = initialOptions.find((o) => o.index === Number(v));
                    if (!opt) return <em>(unavailable)</em>;
                    return <PhaseChipInline p={opt.p} />;
                  },
                }}
              >
                <MenuItem value=""><em>(none)</em></MenuItem>
                {initialOptions.map(({ p, index }) => (
                  <MenuItem key={index} value={index}>
                    <PhaseChipInline p={p} />
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {initialGone && (
              <Typography sx={{ fontSize: 12, color: LegacyTokens.amberText, fontStyle: "italic" }}>
                The previously-chosen initial phase is no longer available (removed or deactivated).
                Pick another phase, or auto-open will disable on save.
              </Typography>
            )}

            {/* Direction — only 'first_waiting' for now, per the client's
               unanswered option list. Rendered as a select with the one
               option rather than inventing others. */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2, minWidth: 220 }}>
                Direction
              </Typography>
              <TextField
                select
                size="small"
                value={autoOpenDirection || "first_waiting"}
                disabled={readOnly}
                onChange={(e) => onChange({ autoOpenDirection: e.target.value })}
                sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { height: 36 } }}
              >
                <MenuItem value="first_waiting">Open first waiting target</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
